<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import Icon from "../Icon.vue";
import IconButton from "../IconButton.vue";
import Button from "../Button.vue";
import Modal from "../Modal.vue";
import Switch from "../Switch.vue";
import StatusChip from "../StatusChip.vue";
import BookModal from "./BookModal.vue";
import {
  schedulingApi,
  type Appointment,
  type AppointmentDetail,
} from "../../api/scheduling";
import { toast } from "../../composables/feedback";
import { useEscapeLayer } from "../../composables/escape";
import { can } from "../../composables/permissions";
import {
  agendaRefs,
  onAppointmentChange,
  providerLabel,
  schedulingError,
  stampText,
  statusLabel,
  statusTone,
  whenText,
} from "../../composables/agenda";
import { t, type MessageKey } from "../../i18n";

/**
 * One appointment (decision 0022, board "Calendario"): when, with whom, where, its status, the
 * team's note, the answers, and what happened to it (who booked, the notices that went or will
 * go, the contact's taps), with the actions: open the conversation, move, cancel, mark attended
 * or missed, confirm on the contact's behalf. `detail` shows a fixed one (the styleguide).
 */
const props = withDefaults(
  defineProps<{
    slug: string;
    appointmentId: string;
    /** Over the contact panel instead of beside the page. */
    overlay?: boolean;
    /** A fixed appointment with its story, without the API (the styleguide). */
    detail?: AppointmentDetail | null;
    now?: Date | null;
  }>(),
  { overlay: false, detail: null, now: null },
);
const emit = defineEmits<{
  close: [];
  changed: [appointment: Appointment];
  loaded: [appointment: Appointment];
}>();
const router = useRouter();
// The styleguide's sample stays on the page: only a real one closes on Escape.
useEscapeLayer(
  () => !props.detail && props.appointmentId,
  () => emit("close"),
);

const data = ref<AppointmentDetail | null>(props.detail);
const refs = agendaRefs(props.slug);
async function load() {
  if (props.detail) {
    data.value = props.detail;
    return;
  }
  try {
    data.value = await schedulingApi.appointment(
      props.slug,
      props.appointmentId,
    );
    notes.value = data.value.appointment.notes ?? "";
    emit("loaded", data.value.appointment);
  } catch (err) {
    toast(schedulingError(err));
    emit("close");
  }
}
watch(
  () => props.appointmentId,
  () => {
    data.value = null;
    void load();
  },
);
let stop: (() => void) | undefined;
onMounted(() => {
  void load();
  if (!props.detail)
    stop = onAppointmentChange((e) => {
      if (e.appointmentId === props.appointmentId) void load();
    });
});
onBeforeUnmount(() => stop?.());

const a = computed(() => data.value?.appointment ?? null);
const canWrite = computed(
  () => Boolean(props.detail) || can(props.slug, "appointments.write"),
);
const started = computed(
  () => !!a.value && new Date(a.value.startsAt) <= (props.now ?? new Date()),
);
const open = computed(
  () => a.value?.status === "booked" || a.value?.status === "confirmed",
);
const type = computed(() => refs.types.find((x) => x.id === a.value?.typeId));
const people = computed(() => {
  const ap = a.value;
  if (!ap) return "";
  const ids = ap.holderIds.length
    ? ap.holderIds
    : ap.memberId
      ? [ap.memberId]
      : [];
  const names = ids.map((id) => refs.people[id]).filter(Boolean);
  return names.length ? names.join(", ") : (ap.memberName ?? ap.agendaName);
});
const answers = computed(() => {
  const ap = a.value;
  if (!ap) return [];
  return Object.entries(ap.answers)
    .filter(([, v]) => v)
    .map(([key, value]) => ({
      key,
      label: type.value?.intake.find((f) => f.key === key)?.label ?? key,
      value,
    }));
});

/** A video call: the link the calendar created, or the type's fixed link as the place. */
const isLink = (v: string) => /^https?:\/\//i.test(v);
const joinUrl = computed(() => {
  const ap = a.value;
  if (!ap) return null;
  return (
    ap.onlineUrl ?? (ap.location && isLink(ap.location) ? ap.location : null)
  );
});

// ---- the story: audit history, notices and calendar events, in time order
interface Step {
  at: string | null;
  text: string;
  done: boolean;
  failed?: boolean;
}
function actorText(kind: string, id: string | null): string {
  const ap = a.value;
  if (kind === "member") {
    const name = id ? refs.people[id] : "";
    return name
      ? t("appointment.by.member", { name })
      : t("appointment.by.team");
  }
  if (kind === "assistant") {
    const name = id ? refs.assistants[id] : "";
    return name
      ? t("appointment.by.assistant", { name })
      : t("appointment.by.assistant_unnamed");
  }
  if (kind === "flow") {
    const name = id ? refs.flows[id] : "";
    return name
      ? t("appointment.by.flow", { name })
      : t("appointment.by.flow_unnamed");
  }
  if (kind === "api_key" || kind === "api") return t("appointment.by.api");
  if (id && ap && id === ap.contactId) return t("appointment.by.contact");
  return t("appointment.by.system");
}
const HISTORY = [
  "booked",
  "rescheduled",
  "cancelled",
  "confirmed",
  "attended",
  "no_show",
];
const NOTICES = [
  "confirmation",
  "confirm_request",
  "reminder",
  "rescheduled",
  "cancelled",
  "no_show",
  "confirm_deadline",
];
const steps = computed<Step[]>(() => {
  const d = data.value;
  if (!d) return [];
  const out: Step[] = [];
  for (const h of d.history) {
    const action = h.action.replace(/^appointment\./, "");
    if (!HISTORY.includes(action)) continue;
    const byContact =
      h.actorId !== null && h.actorId === d.appointment.contactId;
    out.push({
      at: h.at,
      text:
        action === "confirmed" && byContact
          ? t("appointment.history.confirmed_by_contact")
          : t(`appointment.history.${action}` as MessageKey, {
              by: actorText(h.actorKind, h.actorId),
            }),
      done: true,
    });
  }
  for (const n of d.notices) {
    if (!NOTICES.includes(n.kind)) continue;
    const name = t(`appointment.notice.${n.kind}` as MessageKey);
    if (n.status === "sent")
      out.push({
        at: n.sentAt ?? n.dueAt,
        text:
          n.kind === "confirm_deadline"
            ? t("appointment.notice.deadline_passed")
            : t(`appointment.notice.sent.${n.kind}` as MessageKey),
        done: true,
      });
    else if (n.status === "pending")
      out.push({ at: n.dueAt, text: name, done: false });
    else if (n.status === "failed")
      out.push({
        at: n.dueAt,
        text: t("appointment.notice.failed", { name }),
        done: true,
        failed: true,
      });
    else
      out.push({
        at: n.dueAt,
        text: t("appointment.notice.skipped", { name }),
        done: true,
      });
  }
  return out.sort((x, y) => (x.at ?? "").localeCompare(y.at ?? ""));
});
const calendars = computed(() =>
  (data.value?.events ?? []).map((e) => ({
    key: e.provider + e.memberId,
    text: t(
      e.status === "synced"
        ? "appointment.calendar.synced"
        : e.status === "failed"
          ? "appointment.calendar.failed"
          : "appointment.calendar.pending",
      {
        calendar: providerLabel(e.provider),
        name: refs.people[e.memberId] ?? "",
      },
    ),
    failed: e.status === "failed",
  })),
);
// ---- the team's note
const notes = ref(props.detail?.appointment.notes ?? "");
const notesDirty = computed(
  () => !!a.value && (a.value.notes ?? "") !== notes.value,
);
const busy = ref(false);
async function run(
  fn: () => Promise<{ appointment: Appointment }>,
  done: string,
) {
  // The styleguide's sample only shows what would happen.
  if (props.detail) return toast(done);
  busy.value = true;
  try {
    const r = await fn();
    toast(done);
    emit("changed", r.appointment);
    await load();
  } catch (err) {
    toast(schedulingError(err));
  } finally {
    busy.value = false;
  }
}
const saveNotes = () =>
  run(
    () =>
      schedulingApi.setNotes(
        props.slug,
        props.appointmentId,
        notes.value.trim() || null,
      ),
    t("appointment.notes_saved"),
  );
const mark = (status: "confirmed" | "attended" | "no_show") =>
  run(
    () => schedulingApi.setStatus(props.slug, props.appointmentId, status),
    t(`appointment.marked.${status}` as MessageKey),
  );

// ---- cancel and move
const cancelling = ref(false);
const reason = ref("");
const notify = ref(true);
function askCancel() {
  reason.value = "";
  notify.value = true;
  cancelling.value = true;
}
async function cancel() {
  await run(
    () =>
      schedulingApi.cancel(props.slug, props.appointmentId, {
        reason: reason.value.trim() || null,
        notify: notify.value,
      }),
    t("appointment.cancelled_toast"),
  );
  cancelling.value = false;
}
const moving = ref(false);
async function moved(ap: Appointment) {
  moving.value = false;
  emit("changed", ap);
  await load();
}
function openConversation() {
  if (a.value?.conversationId)
    void router.push(`/inbox/${a.value.conversationId}`);
}
</script>
<template>
  <aside
    class="detail-panel appointment-panel"
    :class="{ over: overlay }"
    :aria-label="t('appointment.panel')"
  >
    <header class="panel-header">
      <IconButton
        icon="close"
        :label="t('common.close')"
        @click="emit('close')"
      />
      <h2 class="grow truncate">
        {{ a ? `${a.typeName} · ${a.contactName}` : t("appointment.panel") }}
      </h2>
    </header>
    <div v-if="!a" class="scroll detail-body">
      <p class="empty-hint">{{ t("appointment.loading") }}</p>
    </div>
    <div v-else class="scroll detail-body">
      <section class="detail-section">
        <div class="appointment-status">
          <StatusChip
            :status="statusTone(a.status)"
            :label="statusLabel(a.status)"
          />
          <small class="muted">{{ a.agendaName }}</small>
        </div>
        <dl class="contact-facts appointment-facts">
          <div>
            <dt>{{ t("appointment.when") }}</dt>
            <dd>{{ whenText(a) }}</dd>
          </div>
          <div>
            <dt>{{ t("appointment.with") }}</dt>
            <dd>{{ people }}</dd>
          </div>
          <div>
            <dt>{{ t("appointment.contact") }}</dt>
            <dd>{{ a.contactName }}</dd>
          </div>
          <div v-if="a.location || a.onlineUrl">
            <dt>{{ t("appointment.where") }}</dt>
            <dd>
              <template v-if="a.location && !isLink(a.location)">{{
                a.location
              }}</template>
              <a
                v-if="joinUrl"
                :href="joinUrl"
                target="_blank"
                rel="noopener"
                class="appointment-link"
                ><Icon name="video" :size="16" />{{ t("appointment.join") }}</a
              >
            </dd>
          </div>
          <div v-if="a.status === 'cancelled' && a.cancelReason">
            <dt>{{ t("appointment.cancel_reason") }}</dt>
            <dd>{{ a.cancelReason }}</dd>
          </div>
          <div v-if="a.rescheduleCount">
            <dt>{{ t("appointment.moved") }}</dt>
            <dd>
              {{ t("appointment.moved_count", { count: a.rescheduleCount }) }}
            </dd>
          </div>
        </dl>
        <div v-if="canWrite" class="appointment-actions">
          <Button
            v-if="a.conversationId"
            variant="secondary"
            compact
            icon="chat"
            @click="openConversation"
            >{{ t("appointment.open_conversation") }}</Button
          >
          <Button
            v-if="open"
            variant="secondary"
            compact
            icon="clock"
            @click="moving = true"
            >{{ t("appointment.reschedule") }}</Button
          >
          <Button
            v-if="a.status === 'booked'"
            variant="secondary"
            compact
            icon="check"
            :loading="busy"
            @click="mark('confirmed')"
            >{{ t("appointment.confirm_for_contact") }}</Button
          >
          <template v-if="started && a.status !== 'cancelled'">
            <Button
              v-if="a.status !== 'attended'"
              variant="secondary"
              compact
              icon="checkCircle"
              :loading="busy"
              @click="mark('attended')"
              >{{ t("appointment.attended") }}</Button
            >
            <Button
              v-if="a.status !== 'no_show'"
              variant="secondary"
              compact
              icon="xCircle"
              :loading="busy"
              @click="mark('no_show')"
              >{{ t("appointment.no_show") }}</Button
            >
          </template>
          <Button
            v-if="open"
            variant="ghost"
            compact
            icon="trash"
            class="appointment-cancel"
            @click="askCancel"
            >{{ t("appointment.cancel") }}</Button
          >
        </div>
        <div v-else-if="a.conversationId" class="appointment-actions">
          <Button
            variant="secondary"
            compact
            icon="chat"
            @click="openConversation"
            >{{ t("appointment.open_conversation") }}</Button
          >
        </div>
      </section>

      <section v-if="answers.length" class="detail-section">
        <h3 class="contact-section-title">{{ t("appointment.answers") }}</h3>
        <dl class="contact-facts">
          <div v-for="x in answers" :key="x.key">
            <dt>{{ x.label }}</dt>
            <dd>{{ x.value }}</dd>
          </div>
        </dl>
      </section>

      <section class="detail-section">
        <h3 class="contact-section-title">{{ t("appointment.notes") }}</h3>
        <label class="wa-field"
          ><span>{{ t("appointment.notes_label") }}</span
          ><textarea
            v-model="notes"
            maxlength="2000"
            placeholder=" "
            :disabled="!canWrite"
          ></textarea>
        </label>
        <div v-if="notesDirty && canWrite" class="row appointment-save">
          <Button variant="secondary" compact @click="notes = a.notes ?? ''">{{
            t("common.cancel")
          }}</Button>
          <Button compact :loading="busy" @click="saveNotes">{{
            t("common.save")
          }}</Button>
        </div>
      </section>

      <section class="detail-section">
        <h3 class="contact-section-title">{{ t("appointment.story") }}</h3>
        <ol class="appointment-steps">
          <li
            v-for="(s, i) in steps"
            :key="i"
            :class="{ done: s.done, failed: s.failed }"
          >
            <i aria-hidden="true" /><span
              >{{ s.text
              }}<small v-if="s.at" class="muted">
                · {{ stampText(s.at, a.timezone) }}</small
              ></span
            >
          </li>
          <li
            v-for="c in calendars"
            :key="c.key"
            class="done"
            :class="{ failed: c.failed }"
          >
            <i aria-hidden="true" /><span>{{ c.text }}</span>
          </li>
        </ol>
      </section>
    </div>

    <Modal
      :open="cancelling"
      :title="t('appointment.cancel_title')"
      @close="cancelling = false"
    >
      <div class="modal-form">
        <p class="small">
          {{ t("appointment.cancel_lead", { when: a ? whenText(a) : "" }) }}
        </p>
        <label class="wa-field"
          ><span>{{ t("appointment.cancel_reason_label") }}</span
          ><input v-model="reason" maxlength="300" placeholder=" "
        /></label>
        <Switch
          v-model="notify"
          :label="t('appointment.cancel_notify')"
          :hint="t('appointment.cancel_notify_hint')"
        />
      </div>
      <template #footer>
        <Button variant="secondary" @click="cancelling = false">{{
          t("appointment.cancel_keep")
        }}</Button>
        <Button variant="danger" :loading="busy" @click="cancel">{{
          t("appointment.cancel_confirm")
        }}</Button>
      </template>
    </Modal>
    <BookModal
      v-if="a && !detail"
      :open="moving"
      :slug="slug"
      :appointment="a"
      @close="moving = false"
      @booked="moved"
    />
  </aside>
</template>
